<template>
  <div class="ai">
    <div v-if="loading" class="ai__loading"><v-progress-circular indeterminate size="18" width="2" class="mr-2" />Lade Einstellungen …</div>
    <template v-else>
      <!-- Kopf: Hauptschalter + Status -->
      <v-card class="ai-card ai-card--head">
        <div class="ai-card__row">
          <div class="ai-card__iconBox"><v-icon small>mdi-timer-outline</v-icon></div>
          <div class="ai-card__body">
            <div class="ai-card__title">Auto-Import</div>
            <div class="ai-card__text">
              Live-Tasks, deren Upscale bestanden hat, werden nach einer zufälligen Wartezeit automatisch importiert,
              inklusive Vorschau-Mail an den Kunden. Importiert wird nur zu den Arbeitszeiten.
            </div>
          </div>
          <div class="ai-card__ctl">
            <span class="ai-pill" :class="form.enabled ? 'ai-pill--on' : 'ai-pill--off'">{{ form.enabled ? "Aktiv" : "Pausiert" }}</span>
            <v-switch v-model="form.enabled" inset hide-details dense class="ai-switch" @change="saveFlag('enabled')" />
          </div>
        </div>
      </v-card>

      <!-- Ausschlussgründe -->
      <v-card class="ai-card">
        <div class="ai-card__head">
          <v-icon small class="ai-card__headIcon">mdi-filter-remove-outline</v-icon>
          Ausschlussgründe
          <span class="ai-card__headHint">Schalter an = der Grund hält den Task zurück, er bleibt unter „Manuell". Änderungen gelten sofort.</span>
        </div>
        <div class="ai-rules">
          <div v-for="ex in EXCLUSIONS" :key="ex.key" class="ai-rule" :class="{ 'ai-rule--off': !form.exclusions[ex.key] }">
            <div class="ai-rule__text">
              <div class="ai-rule__label">{{ ex.label }}</div>
              <div class="ai-rule__hint">{{ ex.hint }}</div>
            </div>
            <v-switch v-model="form.exclusions[ex.key]" inset hide-details dense class="ai-switch" @change="saveExclusions" />
          </div>
          <div class="ai-rule" :class="{ 'ai-rule--off': !revisedExcluded }">
            <div class="ai-rule__text">
              <div class="ai-rule__label">Vom Detail-Pass überarbeitet</div>
              <div class="ai-rule__hint">mindestens ein Bereich wurde neu gerendert, z. B. ein Gesicht von 60 auf 90</div>
            </div>
            <v-switch v-model="revisedExcluded" inset hide-details dense class="ai-switch" />
          </div>
        </div>
        <div class="ai-fixed">
          <v-icon x-small class="mr-1">mdi-lock-outline</v-icon>
          Immer manuell: Upscale nicht bestanden · Task noch in Verarbeitung · Server-Import gescheitert · Zeichen ohne Schrift
        </div>
      </v-card>

      <!-- Zeitplan -->
      <v-card class="ai-card">
        <div class="ai-card__head">
          <v-icon small class="ai-card__headIcon">mdi-clock-outline</v-icon>
          Zeitplan
          <span class="ai-card__headHint">Wartezeiten und Arbeitszeiten, Ortszeit Berlin. Gilt nach „Speichern".</span>
        </div>
        <div class="ai-grid">
          <div class="ai-field">
            <div class="ai-field__label">Wartezeit</div>
            <div class="ai-field__range">
              <v-text-field v-model.number="form.delay.min" type="number" min="0" dense outlined hide-details suffix="min" label="von" />
              <v-text-field v-model.number="form.delay.max" type="number" min="0" dense outlined hide-details suffix="min" label="bis" />
            </div>
            <div class="ai-field__hint">zufällig zwischen von und bis nach dem bestandenen Upscale</div>
          </div>
          <div class="ai-field">
            <div class="ai-field__label">Wartezeit Express</div>
            <div class="ai-field__range">
              <v-text-field v-model.number="form.expressDelay.min" type="number" min="0" dense outlined hide-details suffix="min" label="von" />
              <v-text-field v-model.number="form.expressDelay.max" type="number" min="0" dense outlined hide-details suffix="min" label="bis" />
            </div>
            <div class="ai-field__hint">für Express-Produkte</div>
          </div>
          <div class="ai-field">
            <div class="ai-field__label">Mindestabstand Text zum Rand</div>
            <div class="ai-field__range">
              <v-text-field v-model.number="form.textMarginPercent" type="number" min="0" step="0.5" dense outlined hide-details suffix="%" label="der Breite" />
            </div>
            <div class="ai-field__hint">näher am Druckrand = Ausschluss „Text zu nah am Rand"</div>
          </div>
        </div>

        <div class="ai-sub">Arbeitszeiten</div>
        <div class="ai-days">
          <div v-for="day in DAYS" :key="day.key" class="ai-day" :class="{ 'ai-day--off': !form.hours[day.key].active }">
            <v-switch v-model="form.hours[day.key].active" inset hide-details dense class="ai-switch ai-day__switch" />
            <span class="ai-day__name">{{ day.label }}</span>
            <template v-if="form.hours[day.key].active">
              <input v-model="form.hours[day.key].from" type="time" class="ai-time" />
              <span class="ai-day__sep">–</span>
              <input v-model="form.hours[day.key].to" type="time" class="ai-time" />
            </template>
            <span v-else class="ai-day__closed">kein Auto-Import</span>
          </div>
        </div>

        <div class="ai-sub">Ausgeschlossene Tage</div>
        <div class="ai-dates">
          <input v-model="newDate" type="date" class="ai-time" @keyup.enter="addDate" />
          <v-btn small depressed rounded :disabled="!newDate" @click="addDate"><v-icon left small>mdi-plus</v-icon>Hinzufügen</v-btn>
          <v-chip v-for="date in form.excludedDates" :key="date" small close class="ai-dates__chip" @click:close="removeDate(date)">{{ formatDate(date) }}</v-chip>
          <span v-if="!form.excludedDates.length" class="ai-dates__none">keine</span>
        </div>
      </v-card>

      <!-- Speichern -->
      <div class="ai-save" :class="{ 'ai-save--dirty': dirty }">
        <span v-if="validationError" class="ai-save__msg ai-save__msg--error"><v-icon x-small class="mr-1">mdi-alert-circle-outline</v-icon>{{ validationError }}</span>
        <span v-else-if="dirty" class="ai-save__msg">Zeitplan geändert, noch nicht gespeichert</span>
        <span v-else-if="savedAt" class="ai-save__msg ai-save__msg--ok"><v-icon x-small class="mr-1">mdi-check</v-icon>Gespeichert</span>
        <span v-else class="ai-save__msg">Alles gespeichert</span>
        <v-btn color="primary" depressed rounded :loading="saving" :disabled="!dirty || !!validationError" @click="save">Speichern</v-btn>
      </div>
    </template>
  </div>
</template>

<script>
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../../../../fb";

const DAYS = [
  { key: "mon", label: "Montag" },
  { key: "tue", label: "Dienstag" },
  { key: "wed", label: "Mittwoch" },
  { key: "thu", label: "Donnerstag" },
  { key: "fri", label: "Freitag" },
  { key: "sat", label: "Samstag" },
  { key: "sun", label: "Sonntag" },
];
const workday = (active) => ({ active, from: "08:00", to: "18:00" });
// Abschaltbare Ausschlussgründe — Schlüssel wie in autoImport.ts (EXCLUSION_KEYS)
const EXCLUSIONS = [
  { key: "flagged", label: "Task ist geflaggt", hint: "Flagge an der Karte oder im Editor" },
  { key: "orderNote", label: "Order-Note vorhanden", hint: "Kunde hat eine Bestellnotiz geschrieben (Testbestellungen ausgenommen)" },
  { key: "reorder", label: "Nachbestellung", hint: "Kunde hat schon einmal bestellt" },
  { key: "supportTicket", label: "Support-Ticket offen", hint: "Bestellung hat ein offenes Ticket" },
  { key: "hold", label: "Bestellung auf Hold", hint: "Fulfill-Hold an der Bestellung" },
  { key: "textNearEdge", label: "Text zu nah am Rand", hint: "nach dem Mindestabstand unten" },
  { key: "emojis", label: "Emojis oder Sonderzeichen im Text", hint: "nur wenn die Geräte-Schrift aktiv ist" },
  { key: "upscaleFallback", label: "Fallback nach fehlgeschlagenem Upscale-Job", hint: "alter Upscaler ist eingesprungen" },
];
const defaultExclusions = () => Object.fromEntries(EXCLUSIONS.map((e) => [e.key, true]));
// Vorbelegung = DEFAULT_AUTO_IMPORT in firebase_aq/functions-upscaler/src/autoImport.ts
const defaults = () => ({
  enabled: true,
  includeRevised: false,
  delay: { min: 30, max: 90 },
  expressDelay: { min: 20, max: 30 },
  textMarginPercent: 3,
  hours: { mon: workday(true), tue: workday(true), wed: workday(true), thu: workday(true), fri: workday(true), sat: workday(false), sun: workday(false) },
  excludedDates: [],
  exclusions: defaultExclusions(),
});

/** Einstellungen des Auto-Imports (settings/autoImport) — gelesen vom Server-Zeitplaner */
export default {
  name: "AutoImportPanel",
  data() {
    return { DAYS, EXCLUSIONS, loading: true, saving: false, savedAt: 0, form: defaults(), saved: "", newDate: "" };
  },
  computed: {
    /** Ausschluss „vom Detail-Pass überarbeitet" = Gegenteil von includeRevised; speichert sofort wie die anderen Schalter */
    revisedExcluded: {
      get() {
        return !this.form.includeRevised;
      },
      set(v) {
        this.form.includeRevised = !v;
        this.saveFlag("includeRevised");
      },
    },
    dirty() {
      return JSON.stringify(this.form) !== this.saved;
    },
    validationError() {
      const f = this.form;
      for (const r of [f.delay, f.expressDelay]) {
        if (!(r.min >= 0) || !(r.max >= r.min)) return "Wartezeit: bis muss mindestens so groß sein wie von.";
      }
      if (!(f.textMarginPercent >= 0)) return "Mindestabstand: 0 oder größer.";
      for (const day of DAYS) {
        const h = f.hours[day.key];
        if (h.active && !(h.from && h.to && h.to > h.from)) return `${day.label}: Ende muss nach dem Beginn liegen.`;
      }
      return "";
    },
  },
  async created() {
    const snap = await getDoc(doc(db, "settings", "autoImport"));
    const s = snap.exists() ? snap.data() : {};
    const d = defaults();
    this.form = {
      enabled: s.enabled === undefined ? d.enabled : !!s.enabled,
      includeRevised: s.includeRevised === undefined ? d.includeRevised : !!s.includeRevised,
      delay: { ...d.delay, ...(s.delay || {}) },
      expressDelay: { ...d.expressDelay, ...(s.expressDelay || {}) },
      hours: Object.fromEntries(DAYS.map((day) => [day.key, { ...d.hours[day.key], ...((s.hours && s.hours[day.key]) || {}) }])),
      excludedDates: Array.isArray(s.excludedDates) ? [...s.excludedDates].sort() : [],
      textMarginPercent: s.textMarginPercent === undefined ? d.textMarginPercent : Number(s.textMarginPercent),
      exclusions: Object.fromEntries(EXCLUSIONS.map((e) => [e.key, s.exclusions && s.exclusions[e.key] !== undefined ? !!s.exclusions[e.key] : true])),
    };
    this.saved = JSON.stringify(this.form);
    this.loading = false;
  },
  methods: {
    formatDate(date) {
      return new Date(date + "T12:00:00").toLocaleDateString([], { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
    },
    addDate() {
      if (this.newDate && !this.form.excludedDates.includes(this.newDate)) this.form.excludedDates = [...this.form.excludedDates, this.newDate].sort();
      this.newDate = "";
    },
    removeDate(date) {
      this.form.excludedDates = this.form.excludedDates.filter((d) => d !== date);
    },
    /** Ausschlussgründe sofort schreiben (wie die Schalter) */
    async saveExclusions() {
      await setDoc(doc(db, "settings", "autoImport"), { exclusions: { ...this.form.exclusions } }, { merge: true });
      const saved = this.saved ? JSON.parse(this.saved) : {};
      this.saved = JSON.stringify({ ...this.form, ...saved, exclusions: { ...this.form.exclusions } });
    },
    /** Schalter sofort schreiben — der Rest erst mit „Speichern" */
    async saveFlag(key) {
      await setDoc(doc(db, "settings", "autoImport"), { [key]: this.form[key] }, { merge: true });
      const saved = this.saved ? JSON.parse(this.saved) : {};
      this.saved = JSON.stringify({ ...this.form, ...saved, [key]: this.form[key] });
    },
    async save() {
      this.saving = true;
      try {
        await setDoc(doc(db, "settings", "autoImport"), JSON.parse(JSON.stringify(this.form)));
        this.saved = JSON.stringify(this.form);
        this.savedAt = Date.now();
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.ai {
  width: 100%;

  &__loading {
    display: flex;
    align-items: center;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.55);
    padding: 20px 4px;
  }
}

/* Karten wie in den anderen Einstellungen: 14px Radius, weicher Schatten */
.ai-card {
  border-radius: 14px !important;
  padding: 16px 20px 18px;
  margin-bottom: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07) !important;

  &--head {
    padding: 16px 20px;
  }
  &__row {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  &__iconBox {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: #edeafa;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    .v-icon {
      color: #6a4fc4 !important;
    }
  }
  &__body {
    flex: 1;
    min-width: 0;
  }
  &__title {
    font-size: 16px;
    font-weight: 700;
  }
  &__text {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.6);
    margin-top: 2px;
  }
  &__ctl {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    flex: 0 0 auto;
  }
  &__head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0 10px;
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 12px;
  }
  &__headIcon {
    color: #7b5cd6 !important;
    margin-right: 2px;
  }
  &__headHint {
    flex-basis: 100%;
    font-size: 12px;
    font-weight: 400;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 2px;
  }
}

.ai-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 10px;
  border-radius: 999px;

  &--on {
    background: #e4f5eb;
    color: #2f7d52;
  }
  &--off {
    background: #fde7e7;
    color: #c62828;
  }
}

/* Vuetify-Switch kompakt, grün wenn an */
.ai-switch {
  margin: 0 !important;
  padding: 0 !important;
  flex: 0 0 auto;

  ::v-deep .v-input--switch__track {
    opacity: 1;
    color: #d3d7de;
  }
  ::v-deep .v-input--switch__thumb {
    color: #fff;
  }
  ::v-deep &.v-input--is-label-active .v-input--switch__track,
  ::v-deep .v-input--is-label-active .v-input--switch__track {
    color: #3f9d68;
  }
}
::v-deep .ai-switch.v-input--is-label-active .v-input--switch__track {
  color: #3f9d68 !important;
}

/* Ausschlussgründe als Zeilen mit Schalter rechts */
.ai-rules {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 8px 14px;
}
.ai-rule {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 12px;
  background: #f6f7f9;
  transition: background 0.15s ease;

  &--off {
    background: #fafafa;

    .ai-rule__label {
      color: rgba(0, 0, 0, 0.45);
    }
  }
  &__text {
    flex: 1;
    min-width: 0;
  }
  &__label {
    font-size: 13px;
    font-weight: 600;
    color: rgba(0, 0, 0, 0.82);
  }
  &__hint {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 1px;
  }
}
.ai-fixed {
  display: flex;
  align-items: center;
  margin-top: 12px;
  font-size: 11.5px;
  color: rgba(0, 0, 0, 0.5);

  .v-icon {
    color: rgba(0, 0, 0, 0.4) !important;
  }
}

/* Zeitplan */
.ai-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}
.ai-field {
  padding: 12px;
  border-radius: 12px;
  background: #f6f7f9;

  &__label {
    font-size: 12.5px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  &__range {
    display: flex;
    gap: 8px;

    ::v-deep .v-input__slot {
      background: #fff !important;
      border-radius: 10px;
    }
  }
  &__hint {
    font-size: 11.5px;
    color: rgba(0, 0, 0, 0.5);
    margin-top: 6px;
  }
}
.ai-sub {
  font-size: 12.5px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  margin: 18px 0 8px;
}
.ai-days {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 6px 14px;
}
.ai-day {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 12px;
  background: #f6f7f9;

  &--off {
    background: #fafafa;

    .ai-day__name {
      color: rgba(0, 0, 0, 0.45);
    }
  }
  &__name {
    width: 92px;
    font-size: 13px;
    font-weight: 600;
  }
  &__sep,
  &__closed {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}
.ai-time {
  padding: 5px 8px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
}
.ai-dates {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  &__chip {
    margin: 0;
  }
  &__none {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
  }
}

/* Speichern-Leiste */
.ai-save {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  padding: 10px 14px;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06), 0 4px 14px rgba(16, 24, 40, 0.07);
  position: sticky;
  bottom: 12px;

  &--dirty {
    box-shadow: 0 0 0 2px rgba(123, 92, 214, 0.35), 0 4px 14px rgba(16, 24, 40, 0.1);
  }
  &__msg {
    display: inline-flex;
    align-items: center;
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.55);

    &--error {
      color: #c62828;

      .v-icon {
        color: #c62828 !important;
      }
    }
    &--ok {
      color: #2f7d52;

      .v-icon {
        color: #2f7d52 !important;
      }
    }
  }
}
</style>
